/**
 * @module host
 */

import {isAppBridgeAction, isFromApp, validatedActionPayload} from '../actions/validator';
import {throwError, ActionType as ErrorActionType, ErrorAction} from '../actions/Error';
import {addAndRemoveFromCollection} from '../util/collection';
import {MessageTransport} from './';
import {apiClientLoad} from './actions';
import {isValidConfig, throwInvalidConfigError} from './clientValidator';
import {AnyAction, Application, Dispatch, Middleware, MiddlewareAPI, Reducer} from './types';

export const buildMiddleware = <State>(reducers: Reducer<State>): Middleware => {
  const transports: MessageTransport[] = [];
  const subscribers: Function[] = [];

  let store: MiddlewareAPI<any>;
  let appStore: any = {};

  const middleware: any = (hostStore: MiddlewareAPI<any>) => {
    store = hostStore;

    return (next: Dispatch<AnyAction>) => <A extends AnyAction>(action: A) => {
      let finalAction: A | ErrorAction = action;
      if (isAppBridgeAction(action) && !isFromApp(action)) {
        for (const transport of transports) {
          // Validate when action is not from app (example Redux devtools)
          // Actions from app is already validated in Client.ts
          const transportAction: A | ErrorAction = validatedActionPayload(
            action,
            transport.localOrigin,
          );
          transport.dispatch({
            payload: transportAction,
            type: 'dispatch',
          });

          // If action is not valid, final action sent will be error action
          if (transportAction !== action) {
            finalAction = transportAction;
          }
        }
      }
      // TODO: Support multiple app stores? Right now there is only 1 global store
      appStore = reducers(appStore, finalAction);

      return next(finalAction);
    };
  };

  middleware.load = (data: any): Application => {
    const {config} = data;

    store.dispatch(apiClientLoad(config));

    return {
      attach(to) {
        const unsubscribe = to.subscribe(event => {
          const action = event.data;
          const type = action && action.type;
          const payload = action && action.payload;
          const source = action && action.source;

          if (!isValidConfig(source, config)) {
            throwInvalidConfigError(source, config, payload);
          }

          for (const listener of subscribers) {
            listener(action);
          }

          switch (type) {
            case 'dispatch':
              store.dispatch({...payload, source});
              break;

            case 'getState':
              to.dispatch({
                type,
                // tslint:disable-next-line:no-invalid-this
                payload: this.getState(),
              });
              break;

            default:
              throwError(
                ErrorActionType.INVALID_ACTION,
                action,
                'Unknown action type. Expected `dispatch` or `getState`.',
              );
          }
        });

        return addAndRemoveFromCollection(transports, to, unsubscribe);
      },

      dispatch(action) {
        store.dispatch(action);
      },

      getState() {
        return appStore;
      },

      subscribe(listener) {
        return addAndRemoveFromCollection(subscribers, listener);
      },
    };
  };

  return middleware;
};



// WEBPACK FOOTER //
// ./src/host/Middleware.ts